import { notification } from "antd";
import React from "react";

// Lets a few quick option changes settle into one re-render.
const RERENDER_DELAY_MS = 250;

// Keeps a rendered PDF in step with its input. A new input (by identity) re-renders after a short pause; until it's
// done the previous PDF stays available and `rendering` is true.
export const useRenderedPdf = <T>(input: T | null, render: (input: T) => Promise<Blob>) => {
    const [rendered, setRendered] = React.useState<{ blob: Blob; input: T; version: number } | null>(null);
    const request = React.useRef(0);
    const renderRef = React.useRef(render);
    React.useEffect(() => {
        renderRef.current = render;
    });

    React.useEffect(() => {
        if (input === null) return;

        const current = ++request.current;
        const timer = window.setTimeout(() => {
            renderRef
                .current(input)
                .then((blob) => {
                    if (current === request.current) setRendered({ blob, input, version: current });
                })
                .catch(() => {
                    if (current === request.current) {
                        notification.error({ message: "Couldn't render the PDF", description: "Please try again." });
                    }
                });
        }, RERENDER_DELAY_MS);
        return () => window.clearTimeout(timer);
    }, [input]);

    return {
        blob: rendered?.blob ?? null,
        version: rendered?.version ?? 0,
        rendering: input !== null && rendered?.input !== input,
    };
};
